import { LogViewer } from '../log-viewer';
import { type LogItemProps } from '../log-viewer/log-item';
import { formatTime } from '../../../utils/format';
import { getFlowLogIcon } from './get-log-icon';
import { useGetRealTimeLogs } from '../../../hooks/use-get-realtime-logs';
import { BotMessageSquareIcon, CheckCheckIcon, SettingsIcon } from 'lucide-react';
import { LogType } from '../../../types/visualizer';

import { Spinner } from '@@/ui/components';
import { Contact } from '@aws-sdk/client-connect';
import { FlowLogEvent } from '../../../types/flow-log';
import { InteractionLogEntry } from '../../../types/interaction-log-data';

interface QAssistantLogEvent {
  assistant_id: string;
  event_timestamp: number;
  event_type: string;
  session_id: string;
  session_name?: string;
  connect_user_arn?: string;
  ai_agent_id?: string;
  completion?: string;
  generation_id?: string;
  intent?: string;
  intent_clicked?: number;
  intent_id?: string;
  is_recommendation_useful?: number;
  is_valid_trigger?: number;
  issue_probability?: string;
  model_id?: string;
  parsed_response?: string;
  prompt?: string;
  prompt_type?: string;
  recommendation?: string;
  recommendation_id?: string;
  response?: string;
  session_event_id?: string;
  session_event_ids?: string[];
  session_message_id?: string;
  utterance?: string;
  Results?: string;
}

interface PromptSystemMessage {
  cachePoint?: boolean;
  [key: string]: unknown;
}

interface PromptMessage {
  role: 'user' | 'assistant' | 'system' | string;
  [key: string]: unknown;
}

export const QAssistantLogViewer = (props: { contact: Contact }) => {
  const sessionId = props.contact.WisdomInfo?.SessionArn?.split('/')[2];
  const { data, isLoading } = useGetRealTimeLogs<'Q_ASSISTANT'>({
    contactId: props.contact.Id!,
    kind: 'Q_ASSISTANT',
    sessionId: sessionId!,
    startAt: props.contact.InitiationTimestamp!,
    endAt: props.contact.DisconnectTimestamp!
  });

  if (isLoading) {
    return (
      <div className='flex items-center justify-center h-full text-neutral-500'>
        <Spinner />
      </div>
    );
  }

  if (!data?.events.length) {
    return (
      <div className='flex items-center justify-center h-full text-neutral-500'>No Q Assistant Logs available</div>
    );
  }

  const logItems: LogItemProps[] = [];

  (data.events as unknown as QAssistantLogEvent[]).forEach((event: QAssistantLogEvent) => {
    const getEventTitle = (eventType: string) => {
      switch (eventType) {
        case 'TRANSCRIPT_CREATE_SESSION':
          return 'Session Created';
        case 'TRANSCRIPT_INTENT_TRIGGERING_REFERENCE':
          return 'Intent Triggered';
        case 'TRANSCRIPT_LARGE_LANGUAGE_MODEL_INVOCATION':
          return 'LLM Invocation';
        case 'TRANSCRIPT_QUERY_ASSISTANT':
          return 'Query Assistant';
        case 'TRANSCRIPT_RECOMMENDATION':
          return 'Recommendation';
        case 'TRANSCRIPT_RESULT_FEEDBACK':
          return 'Result Feedback';
        case 'TRANSCRIPT_SELF_SERVICE_MESSAGE':
          return 'Self Service Message';
        case 'TRANSCRIPT_SESSION_POLLED':
          return 'Session Polled';
        case 'TRANSCRIPT_TRIGGER_DETECTION_MODEL_INVOCATION':
          return 'Trigger Detection';
        case 'TRANSCRIPT_UTTERANCE':
          return 'Utterance';
        default:
          return eventType;
      }
    };

    console;
    logItems.push({
      timestamp: formatTime(new Date(event.event_timestamp).toISOString()),
      title: getEventTitle(event.event_type),
      subtitle: JSON.stringify(event, null, 2),
      parameters: {},
      identifier: event.generation_id,
      result: undefined,
      icon: getFlowLogIcon(event.event_type, event as unknown as FlowLogEvent),
      indentLevel: 0,
      grouped: false,
      raw: event as unknown as FlowLogEvent,
      index: logItems.length,
      customButtons: [],
      accountId: '',
      instanceAlias: ''
    });

    // For SELF_SERVICE_MESSAGE events, add nested message items
    if (event.event_type === 'TRANSCRIPT_SELF_SERVICE_MESSAGE' && event.prompt) {
      try {
        const promptData = JSON.parse(event.prompt);

        if (promptData.system && Array.isArray(promptData.system)) {
          promptData.system.forEach((systemMessage: PromptSystemMessage, sysIndex: number) => {
            if (systemMessage.cachePoint) {
              return;
            }

            logItems.push({
              timestamp: formatTime(new Date(event.event_timestamp - 1000 - sysIndex * 100).toISOString()),
              title: 'System',
              subtitle: JSON.stringify(systemMessage, null, 2),
              parameters: systemMessage as Record<string, unknown>,
              identifier: `sys-${sysIndex}`,
              result: undefined,
              icon: <SettingsIcon className='size-4 text-neutral-600' />,
              indentLevel: 1,
              grouped: true,
              raw: systemMessage as unknown as InteractionLogEntry,
              index: logItems.length,
              accountId: '',
              instanceAlias: ''
            });
          });
        }

        if (promptData.messages && Array.isArray(promptData.messages)) {
          promptData.messages.forEach((message: PromptMessage, msgIndex: number) => {
            const getMessageTitle = (msg: PromptMessage) => {
              if (msg.role === 'user') return 'User';
              if (msg.role === 'assistant') return 'Assistant';
              if (msg.role === 'system') return 'System';
              return `${msg.role} Message`;
            };

            logItems.push({
              timestamp: formatTime(
                new Date(event.event_timestamp - (promptData.messages.length - msgIndex) * 100).toISOString()
              ),
              title: getMessageTitle(message),
              subtitle: JSON.stringify(message, null, 2),
              parameters: message as Record<string, unknown>,
              identifier: `msg-${msgIndex}`,
              result: undefined,
              icon: <BotMessageSquareIcon className='size-4 text-slate-600' />,
              indentLevel: 1,
              grouped: true,
              raw: message as unknown as InteractionLogEntry,
              index: logItems.length,
              accountId: '',
              instanceAlias: ''
            });
          });
        }

        if (event.completion) {
          let completionText: string;
          try {
            completionText = JSON.parse(event.completion);
          } catch (parseError) {
            completionText = event.completion;
          }

          logItems.push({
            timestamp: formatTime(new Date(event.event_timestamp + 200).toISOString()),
            title: 'Completion',
            subtitle: JSON.stringify(completionText, null, 2),
            parameters: {
              Text: completionText
            },
            identifier: 'completion',
            result: undefined,
            icon: <CheckCheckIcon className='size-4 text-green-800' />,
            indentLevel: 1,
            grouped: true,
            raw: { Text: completionText } as unknown as InteractionLogEntry,
            index: logItems.length,
            accountId: '',
            instanceAlias: ''
          });
        }
      } catch (error) {
        console.error('Failed to parse prompt messages:', error);
      }
    }
  });

  return <LogViewer logs={logItems} logType={LogType.INTERACTION_LOG} emptyMessage='No Q Assistant Logs available' />;
};
